// OIDC authorization-code + PKCE client for the `clms-spa` public client (E1 US-1.1).
//
// The three legs of the flow live here:
//   1. beginLogin  — stash a fresh PKCE verifier + state, redirect to Keycloak;
//   2. handleCallback — validate state, exchange the code (+verifier) for tokens;
//   3. getValidAccessToken / refresh — keep a live access token for API calls.
// Plus buildLogoutUrl for RP-initiated (single) logout. Keycloak is reached with
// plain fetch (not lib/api) — these are IdP endpoints, not the CLMS API — and the
// verifier/state ride in sessionStorage so they survive the redirect but not a new tab.

import { config } from "../config";
import { codeChallengeS256, randomUrlSafe } from "./pkce";
import {
  compareAndSetTokens,
  getIdToken,
  getTokens,
  setTokens,
  type StoredTokens,
} from "./tokenStore";
import type { Session } from "./session";

const AUTHORIZE_PATH = "/protocol/openid-connect/auth";
const TOKEN_PATH = "/protocol/openid-connect/token";
const LOGOUT_PATH = "/protocol/openid-connect/logout";
const PKCE_STORAGE_KEY = "clms.oidc.pkce";
// Refresh slightly before the real expiry so an in-flight request never races a 401.
const EXPIRY_SKEW_MS = 30_000;

function issuer(): string {
  return config.oidcIssuer.replace(/\/+$/, "");
}

/** Whether an IdP is configured; when not, callers use the dev placeholder session. */
export function isSsoConfigured(): boolean {
  return config.oidcIssuer.trim().length > 0;
}

/**
 * Callback URL Keycloak redirects back to. Must match a realm redirect URI
 * (`CLMS_SPA_REDIRECT_URIS`, dev default `http://localhost:5173/*`). A dedicated
 * public route keeps the `?code=` off guarded pages (which would bounce to /login
 * before the exchange runs).
 */
export function redirectUri(): string {
  return `${window.location.origin}/auth/callback`;
}

interface PkceState {
  verifier: string;
  state: string;
  returnTo: string;
  tenant: string | null;
}

export interface BeginLoginParams {
  /** Resolved tenant slug (null on the bare apex host in dev). */
  tenant: string | null;
  /** Where to land after login completes. */
  returnTo: string;
  /** Active UI language, carried into the IdP via ui_locales (US-1.1). */
  locale: string;
  /**
   * The realm IdP alias this tenant's people authenticate at, sent as `kc_idp_hint` — or null to let
   * Keycloak draw its own login page, which is the right answer for a tenant with no federation and for
   * the operator console (ADR-0014 §12 C-6).
   *
   * Resolved by `auth/tenantBranding.fetchIdpHint` and NOT derived here: the alias comes from the backend
   * (`GET /api/auth/tenant`), which answers it only while the arrangement is actually in force.
   *
   * Required, with no default, so that adding a sign-in surface is a decision about where its people
   * authenticate. A defaulted field would make the federated case the one that silently breaks: omitting
   * it compiles, works for every tenant without a federation, and sends a customer's people to a generic
   * page that does not offer their provider.
   */
  idpHint: string | null;
}

/** Start the authorization-code + PKCE flow: stash the verifier, then redirect. */
export async function beginLogin(params: BeginLoginParams): Promise<void> {
  const verifier = randomUrlSafe();
  const state = randomUrlSafe(16);
  const challenge = await codeChallengeS256(verifier);
  const pkce: PkceState = {
    verifier,
    state,
    returnTo: params.returnTo,
    tenant: params.tenant,
  };
  sessionStorage.setItem(PKCE_STORAGE_KEY, JSON.stringify(pkce));

  const query = new URLSearchParams({
    client_id: config.oidcClientId,
    response_type: "code",
    scope: "openid profile email",
    redirect_uri: redirectUri(),
    code_challenge: challenge,
    code_challenge_method: "S256",
    state,
    ui_locales: params.locale,
  });
  // The tenant travels to the login page so branding/theme resolve there.
  if (params.tenant) query.set("tenant", params.tenant);
  applyIdpHint(query, params.idpHint);
  window.location.assign(`${issuer()}${AUTHORIZE_PATH}?${query.toString()}`);
}

/**
 * Put the tenant's `kc_idp_hint` on an authorization request, when there is one (ADR-0014 §12 C-6).
 *
 * With it Keycloak answers 303 straight to that tenant's broker and the person authenticates at their
 * employer's own directory; without it the realm's generic login page renders, and a per-tenant broker is
 * `hideOnLogin` so it is not offered there — which is the design and not a gap, because one customer's
 * identity provider has no business being visible to another customer's people.
 *
 * Shared by both legs below for a reason that is not tidiness: a step-up that dropped the hint would send a
 * federated person to a realm credential page for a password they do not have, so first sign-in is NOT the
 * only authorization request it belongs on.
 *
 * <b>But it does not belong on every one of them</b> (ADR-0018 §6.3, corrected here — this comment used to
 * say it did). The rule is about WHOSE credential is being re-checked, not about which tenant the request
 * is for: an Incedo consultant inside a federated customer is realm-local, so a hint would push them into
 * that customer's broker, where `CLMS first broker login` refuses them rather than linking them. So the
 * hint is resolved by the CALLER and may legitimately be null for a non-federated tenant, for the operator
 * console (which addresses no tenant at all) and for a session whose tenant was reached by a membership —
 * see `auth/stepUp.ts`, which is where that last question is asked. This function's job is only to put a
 * resolved hint on the request and to leave a null one off.
 */
function applyIdpHint(query: URLSearchParams, idpHint: string | null): void {
  if (idpHint) query.set("kc_idp_hint", idpHint);
}

/**
 * Step-up re-authentication for a sensitive action (US-1.4, audit-privacy-api.md §6). Same
 * authorization-code + PKCE flow as {@link beginLogin}, but forces a *fresh* credential prompt
 * (`prompt=login`, `max_age=0`) so Keycloak stamps a new `auth_time` the backend accepts as
 * step-up. The callback lands back on `returnTo`, now holding a fresh token; the caller re-runs
 * the sensitive action, which the backend then permits. Only reachable when SSO is configured —
 * in the dev/E2E placeholder-session mode there is no IdP to re-challenge (see auth/stepUp.ts).
 */
export async function beginStepUp(params: BeginLoginParams): Promise<void> {
  const verifier = randomUrlSafe();
  const state = randomUrlSafe(16);
  const challenge = await codeChallengeS256(verifier);
  const pkce: PkceState = {
    verifier,
    state,
    returnTo: params.returnTo,
    tenant: params.tenant,
  };
  sessionStorage.setItem(PKCE_STORAGE_KEY, JSON.stringify(pkce));

  const query = new URLSearchParams({
    client_id: config.oidcClientId,
    response_type: "code",
    scope: "openid profile email",
    redirect_uri: redirectUri(),
    code_challenge: challenge,
    code_challenge_method: "S256",
    state,
    ui_locales: params.locale,
    // Force a fresh interactive re-auth even though a session exists → new auth_time.
    prompt: "login",
    max_age: "0",
  });
  if (params.tenant) query.set("tenant", params.tenant);
  // A federated customer's OWN person has their credential at their employer's directory, not in this
  // realm, so a re-auth that withheld the hint would challenge them for a password they have never had —
  // and a consultant who reached that same tenant by a membership is the opposite case, which is why the
  // hint is the caller's to resolve and arrives null for them (ADR-0018 §6.3, `auth/stepUp.ts`).
  applyIdpHint(query, params.idpHint);
  window.location.assign(`${issuer()}${AUTHORIZE_PATH}?${query.toString()}`);
}

interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  expires_in?: number;
  token_type?: string;
}

/** The stored shape of a token response — minted, not yet written. */
function tokensFrom(res: TokenResponse): StoredTokens {
  return {
    accessToken: res.access_token,
    refreshToken: res.refresh_token,
    idToken: res.id_token,
    expiresAt: Date.now() + (res.expires_in ?? 60) * 1000,
  };
}

/**
 * Store a response unconditionally. For the code exchange only: its caller IS the authority on which
 * session this document holds, so a pair it just minted supersedes whatever was there. The refresh grant
 * is the other case and writes through `compareAndSetTokens` instead — see {@link refresh}.
 */
function persist(res: TokenResponse): StoredTokens {
  const tokens = tokensFrom(res);
  setTokens(tokens);
  return tokens;
}

async function postToken(body: URLSearchParams): Promise<Response> {
  return fetch(`${issuer()}${TOKEN_PATH}`, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });
}

export class OidcError extends Error {}

export interface CallbackResult {
  session: Session;
  /** Where to navigate now that the session is established. */
  returnTo: string;
}

/** Cleared once {@link handleCallback} has settled, win or lose. See {@link codeExchangePending}. */
let codeExchangeSettled = false;

/** Told once, when {@link codeExchangePending} stops being true. See {@link subscribeCodeExchange}. */
const codeExchangeListeners = new Set<() => void>();

/**
 * Be told when {@link codeExchangePending} stops answering true, so a caller that DEFERRED a call can make
 * it — in this document, on the token the exchange delivered.
 *
 * WHY A SUBSCRIPTION AND NOT A SECOND LOOK LATER. The callers of `codeExchangePending` are React effects
 * above the router, and nothing in their inputs changes when the exchange finishes: `AuthCallbackPage`
 * establishes the session for the SAME person it booted on (`setSession` with an identical `userId` when a
 * previous session was in storage — a step-up re-authentication, or a returning browser signing in again),
 * then navigates WITHIN the same document. An effect keyed on who is signed in and which tenant they are
 * in therefore never runs again, and a deferral taken on the way in would be permanent until the next
 * document — which is a reconciliation silently not made, not a reconciliation postponed (task 2625efef,
 * round 1 shipped exactly that). So the settlement is a store a component can subscribe to
 * (`useSyncExternalStore`), and the deferral is released by a re-render rather than by hoping for one.
 *
 * Listeners run AFTER the tokens are persisted (`persist` inside the exchange) and BEFORE
 * `AuthCallbackPage`'s `setSession`/`navigate`, so a call made from one carries the fresh access token:
 * `lib/api` attaches what `tokenStore` holds, which is already the new pair.
 */
export function subscribeCodeExchange(onSettled: () => void): () => void {
  codeExchangeListeners.add(onSettled);
  return () => {
    codeExchangeListeners.delete(onSettled);
  };
}

/**
 * Whether this document is a callback whose authorization code has NOT been exchanged yet — i.e. the
 * tokens in storage are still the PREVIOUS login's, and a fresher pair is already on its way.
 *
 * WHY ANYTHING NEEDS TO ASK. The callback document boots like any other: `SessionProvider` seeds itself
 * from the stored session (auth/session.tsx) and the components ABOVE THE ROUTER start reading, all of it
 * before `AuthCallbackPage`'s effect has traded the code for tokens. Those reads therefore carry the token
 * the previous login left behind, and for a few dozen milliseconds the app is making tenant-scoped calls
 * for a session it is in the middle of replacing.
 *
 * Harmless for a read that merely comes back stale. NOT harmless for a call the backend answers with the
 * second-factor challenge (ADR-0021 §6.2), because the SPA answers that one by REDIRECTING for a whole new
 * authorization — and `beginStepUp` writes a fresh PKCE verifier over the one this very document came back
 * with, so the exchange in flight can no longer complete and the token it was about to deliver is lost.
 * Measured on main (run 41334510350, task 2625efef): the second re-authentication's token arrived carrying
 * `amr: ["pwd","otp"]` — the answer — and 48 ms earlier `PreferredLanguageSync` had already sent
 * `POST /api/profile/language/reconcile` on the PREVIOUS token. Its 401 drove a third re-authentication
 * nobody answered, for a refusal that was about a token already replaced.
 *
 * So a caller above the router asks this before making a tenant-scoped call it cannot usefully make yet —
 * and subscribes with {@link subscribeCodeExchange}, because a deferral nothing releases is the call never
 * being made at all. Read off the URL rather than from a flag the callback page sets, because those calls
 * go out before that page's effect runs.
 */
export function codeExchangePending(): boolean {
  if (codeExchangeSettled || typeof window === "undefined") return false;
  const url = new URL(window.location.href);
  return url.pathname.endsWith("/auth/callback") && url.searchParams.has("code");
}

/**
 * Handle the /auth/callback redirect: validate the CSRF `state`, then exchange the
 * authorization code + PKCE verifier for tokens. Throws OidcError on any failure so
 * the callback page can show the error state instead of a half-established session.
 */
export async function handleCallback(search: string): Promise<CallbackResult> {
  try {
    return await exchangeCallback(search);
  } finally {
    // Win or lose: there is no exchange left to protect. On the error path the page renders its own error
    // state and establishes no session, so a later refusal is the app's to answer as usual.
    codeExchangeSettled = true;
    // And whoever deferred a call is told, here rather than on the next document: this is the moment the
    // token the call should carry exists (the win) or is never coming (the loss). Releasing on the loss
    // too is safe for the same reason the flag is cleared on it — for any document that carried a code,
    // which is the only kind held back at all, the verifier has already been spent and removed, so there
    // is no exchange left for a refusal's redirect to strand.
    // Copied, so a listener that unsubscribes in response does not mutate the set being walked.
    for (const onSettled of [...codeExchangeListeners]) onSettled();
  }
}

async function exchangeCallback(search: string): Promise<CallbackResult> {
  const params = new URLSearchParams(search);
  const err = params.get("error");
  if (err) throw new OidcError(params.get("error_description") ?? err);

  const code = params.get("code");
  const state = params.get("state");
  const rawPkce = sessionStorage.getItem(PKCE_STORAGE_KEY);
  sessionStorage.removeItem(PKCE_STORAGE_KEY); // one-time use, win or lose

  if (!code || !state || !rawPkce) throw new OidcError("missing_authorization_code");
  const pkce = JSON.parse(rawPkce) as PkceState;
  if (state !== pkce.state) throw new OidcError("state_mismatch");

  const res = await postToken(
    new URLSearchParams({
      grant_type: "authorization_code",
      client_id: config.oidcClientId,
      code,
      redirect_uri: redirectUri(),
      code_verifier: pkce.verifier,
    }),
  );
  if (!res.ok) throw new OidcError(`token_exchange_failed_${res.status}`);

  const tokens = persist((await res.json()) as TokenResponse);
  return { session: sessionFromTokens(tokens, pkce.tenant), returnTo: pkce.returnTo || "/" };
}

interface TokenClaims {
  sub?: string;
  name?: string;
  preferred_username?: string;
  email?: string;
  tenant_id?: string;
  /** Keycloak carries realm roles here (on the access token, not the id token). */
  realm_access?: { roles?: string[] };
  /** And client roles here, one entry per client: `resource_access[clientId].roles`. */
  resource_access?: Record<string, { roles?: string[] } | undefined>;
  // NO `groups`. ADR-0018 §3.2 carried the tenant memberships here, one `/tenants/<uuid>/<ROLE>` path per
  // entry, and ADR-0021 §4.1 moved that carrier into `tenant_memberships`. A realm whose mapper has not
  // been retired yet still mints the claim (§11 — the mapper's removal is cleanup, not a cutover), so this
  // is not an omission to be fixed by reading it again: a membership session's roles come from
  // `GET /api/auth/memberships` (§5, §9) and nothing in this SPA reads that claim any more.
}

/** Decode a JWT payload (no signature check — the backend validates; this is display-only). */
function decodeJwt(token: string | undefined): TokenClaims {
  const part = token?.split(".")[1];
  if (!part) return {};
  try {
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const bytes = atob(b64);
    const utf8 = decodeURIComponent(
      Array.from(bytes)
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join(""),
    );
    return JSON.parse(utf8) as TokenClaims;
  } catch {
    return {};
  }
}

/**
 * Derive the app session from the token claims. The token carries `tenant_id` (a
 * GUID), not the URL slug, so the slug still comes from the address the user is on
 * (resolveTenantSlug) — null on the bare apex host in dev, exactly as before.
 */
export function sessionFromTokens(tokens: StoredTokens, tenantHint: string | null): Session {
  const claims = decodeJwt(tokens.idToken ?? tokens.accessToken);
  // Roles live in the access token, not the id token — decode the access token for
  // roles even though display fields prefer the id token.
  const accessClaims = decodeJwt(tokens.accessToken);
  const roles = rolesFromAccessToken(accessClaims);
  return {
    userId: claims.sub ?? "",
    displayName: claims.name ?? claims.preferred_username ?? claims.email ?? "",
    // The account's own address, kept as well as folded into the display name above. One screen needs it
    // as an ADDRESS rather than as a label — the test invitation's "this goes to you, here" (ADR-0027
    // §7.2) — and `displayName` cannot serve that, since it is the `name` claim whenever there is one.
    // Absent rather than empty when the token carried no `email`: see Session.email on what it is not.
    email: claims.email,
    tenantSlug: tenantHint,
    // The CLIENT roles, which are the account's own tenant's and travel nowhere else (ADR-0018 §4.2). A
    // tenant reached by MEMBERSHIP takes its roles from the selection instead, which no token can answer:
    // the choice is made after the token exists (§6.1) and the carrier is a row, not a claim (ADR-0021
    // §4.1). `auth/capabilities.ts` is where those two meet.
    roles,
  };
}

/**
 * The caller's role codes, read from the carriers ADR-0014 §4.1 put them on: the backend API's own client
 * and the operator console's client. The union with `realm_access` is deliberate and is what makes the
 * carrier migration need no flag day — during it a person holds the old realm role, the new client role,
 * or both, and all three read the same here.
 *
 * Only the two NAMED clients are read, never every key under `resource_access`. The flattener in the
 * backend takes the same position for the same reason (§4.2): the day another application defines a client
 * role called `HR`, a blanket read would show this person an HR nav they have no authority behind.
 */
function rolesFromAccessToken(claims: TokenClaims): string[] {
  const fromClients = [config.apiClientId, config.platformClientId]
    .filter((clientId, i, all) => clientId && all.indexOf(clientId) === i)
    .flatMap((clientId) => claims.resource_access?.[clientId]?.roles ?? []);
  return [...new Set([...(claims.realm_access?.roles ?? []), ...fromClients])];
}

// Single-flight guard so concurrent API calls that all see an expired token trigger
// exactly one refresh round-trip, not one per call.
let refreshInFlight: Promise<string | undefined> | null = null;

/**
 * Spend `held`'s refresh grant, and write the outcome over `held` ONLY while it is still the pair the store
 * holds.
 *
 * BOTH WRITES ARE CONDITIONAL, and that is the whole point of taking the pair rather than just its grant.
 * This function decides what to write before the round-trip and applies it afterwards, so by then the store
 * may hold a pair this refresh knows nothing about — the callback document is the window where it does
 * (ADR-0021 §6.2, `codeExchangePending` above, and the long remark on `compareAndSetTokens`). Clearing
 * there would throw away a session established milliseconds ago; persisting there would put a token minted
 * from the OLD grant over the new one.
 *
 * A caller that loses the race is answered with the pair now held, NOT with undefined: undefined means "no
 * bearer header" to `lib/api`, and a bearer-less request is precisely what turns this race into a plain 401
 * and a bounce to the login page. undefined is reserved for the case where the clear actually happened.
 */
async function refresh(held: StoredTokens, refreshToken: string): Promise<string | undefined> {
  const res = await postToken(
    new URLSearchParams({
      grant_type: "refresh_token",
      client_id: config.oidcClientId,
      refresh_token: refreshToken,
    }),
  );
  if (!res.ok) {
    // Refresh rejected (expired/revoked) — force a fresh login, unless this pair is no longer the one held.
    const outcome = compareAndSetTokens(held, null);
    return outcome.written ? undefined : outcome.tokens?.accessToken;
  }
  const minted = tokensFrom((await res.json()) as TokenResponse);
  const outcome = compareAndSetTokens(held, minted);
  return outcome.written ? minted.accessToken : outcome.tokens?.accessToken;
}

/**
 * Current access token for an API call, refreshing first when it is expired or
 * within the skew window. Returns undefined when no tokens are held (e.g. an E2E or
 * email-link session), letting the caller send the request without a Bearer header.
 */
export async function getValidAccessToken(): Promise<string | undefined> {
  const tokens = getTokens();
  if (!tokens) return undefined;
  if (Date.now() < tokens.expiresAt - EXPIRY_SKEW_MS) return tokens.accessToken;
  const { refreshToken } = tokens;
  if (!refreshToken) return tokens.accessToken; // no refresh grant — let the 401 hook fire
  if (!refreshInFlight) {
    // The pair travels with the grant: it is what the write at the other end is compared against.
    refreshInFlight = refresh(tokens, refreshToken).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/**
 * RP-initiated logout URL (Keycloak end-session), or null when OIDC is not
 * configured — in which case the caller just clears local state. The id_token_hint
 * lets Keycloak end its own session; post_logout_redirect_uri returns to /login.
 */
export function buildLogoutUrl(postLogoutPath = "/login"): string | null {
  if (!isSsoConfigured()) return null;
  const query = new URLSearchParams({
    client_id: config.oidcClientId,
    post_logout_redirect_uri: `${window.location.origin}${postLogoutPath}`,
  });
  const idToken = getIdToken();
  if (idToken) query.set("id_token_hint", idToken);
  return `${issuer()}${LOGOUT_PATH}?${query.toString()}`;
}
